import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import LogoUploader from "@/components/LogoUploader";

/**
 * Admin profile editor. Reuses one dialog for both business and customer
 * accounts — fields render based on `user.role`. Submits a PATCH to
 * /admin/users/{id} which is field-by-field idempotent (only provided keys
 * are written, normalized variants recomputed server-side).
 */
const PRICE_TIERS = ["", "$", "$$", "$$$", "$$$$"];

export default function AdminEditUserDialog({ user, open, onOpenChange, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => buildInitialState(user));

  useEffect(() => {
    setForm(buildInitialState(user));
  }, [user]);

  if (!user) return null;
  const isBusiness = user.role === "business";

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = buildPayload(form, user);
      const { data } = await api.patch(`/admin/users/${user.id}`, payload);
      toast.success(
        data.updated?.length
          ? `Updated ${data.updated.length} field${data.updated.length === 1 ? "" : "s"}`
          : "No changes"
      );
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-sm max-w-2xl max-h-[88vh] overflow-y-auto"
        data-testid="admin-edit-user-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            Edit {isBusiness ? "business" : "customer"} profile
          </DialogTitle>
          <DialogDescription>
            Admin override — changes save immediately and bypass owner
            verification. Normalized search fields are recomputed automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-1">
          {/* Shared fields */}
          <FieldRow>
            <Field label={isBusiness ? "Owner name" : "Customer name"}>
              <Input
                value={form.name}
                onChange={(e) => update({ name: e.target.value })}
                className="rounded-md"
                data-testid="admin-edit-name"
              />
            </Field>
            <Field label="Account email">
              <Input
                type="email"
                value={form.email}
                onChange={(e) => update({ email: e.target.value })}
                className="rounded-md"
                data-testid="admin-edit-email"
              />
            </Field>
          </FieldRow>

          {isBusiness && (
            <>
              <div className="border border-border rounded-md p-4 bg-gray-50/40">
                <Label className="text-xs font-mono uppercase tracking-wider text-gray-500">
                  Company logo
                </Label>
                <p className="text-[11px] text-gray-500 mt-0.5 mb-3">
                  PNG / JPG / WebP. Auto-resized to 300×300 and shown on the
                  business page and directory listing.
                </p>
                <LogoUploader
                  value={form.logo_data_url}
                  onChange={(v) => update({ logo_data_url: v || "" })}
                />
              </div>

              <FieldRow>
                <Field label="Business name" full>
                  <Input
                    value={form.business_name}
                    onChange={(e) => update({ business_name: e.target.value })}
                    className="rounded-md"
                    data-testid="admin-edit-business-name"
                  />
                </Field>
              </FieldRow>

              <FieldRow>
                <Field label="Street address" full>
                  <Input
                    value={form.business_address}
                    onChange={(e) => update({ business_address: e.target.value })}
                    className="rounded-md"
                    data-testid="admin-edit-business-address"
                  />
                </Field>
              </FieldRow>

              <FieldRow>
                <Field label="City">
                  <Input
                    value={form.business_city}
                    onChange={(e) => update({ business_city: e.target.value })}
                    className="rounded-md"
                    data-testid="admin-edit-business-city"
                  />
                </Field>
                <Field label="State">
                  <Input
                    value={form.business_state}
                    onChange={(e) => update({ business_state: e.target.value })}
                    className="rounded-md"
                    data-testid="admin-edit-business-state"
                  />
                </Field>
                <Field label="ZIP">
                  <Input
                    value={form.business_zip}
                    onChange={(e) => update({ business_zip: e.target.value })}
                    className="rounded-md"
                    data-testid="admin-edit-business-zip"
                  />
                </Field>
              </FieldRow>

              <FieldRow>
                <Field label="Phone">
                  <Input
                    value={form.phone}
                    onChange={(e) => update({ phone: e.target.value })}
                    className="rounded-md"
                    data-testid="admin-edit-phone"
                  />
                </Field>
                <Field label="Website">
                  <Input
                    value={form.website}
                    onChange={(e) => update({ website: e.target.value })}
                    placeholder="https://"
                    className="rounded-md"
                    data-testid="admin-edit-website"
                  />
                </Field>
              </FieldRow>

              <FieldRow>
                <Field label="Public contact email">
                  <Input
                    type="email"
                    value={form.contact_email}
                    onChange={(e) => update({ contact_email: e.target.value })}
                    className="rounded-md"
                    data-testid="admin-edit-contact-email"
                  />
                </Field>
                <Field label="Established (YYYY)">
                  <Input
                    value={form.date_established}
                    onChange={(e) => update({ date_established: e.target.value })}
                    placeholder="2018"
                    className="rounded-md"
                    data-testid="admin-edit-established"
                  />
                </Field>
                <Field label="Price tier">
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {PRICE_TIERS.map((t) => (
                      <button
                        key={t || "none"}
                        type="button"
                        onClick={() => update({ price_tier: t })}
                        className={`px-3 py-1.5 rounded-[35px] text-xs font-mono border ${
                          form.price_tier === t
                            ? "bg-[#B31E1E] text-white border-[#B31E1E]"
                            : "bg-white text-gray-700 border-border hover:bg-gray-50"
                        }`}
                        data-testid={`admin-edit-price-${t || "none"}`}
                      >
                        {t || "—"}
                      </button>
                    ))}
                  </div>
                </Field>
              </FieldRow>

              <FieldRow>
                <Field label="Description" full>
                  <Textarea
                    rows={4}
                    value={form.description}
                    onChange={(e) => update({ description: e.target.value })}
                    className="rounded-md"
                    data-testid="admin-edit-description"
                  />
                </Field>
              </FieldRow>

              <div className="flex items-center justify-between border border-border rounded-md px-3 py-2.5 bg-gray-50">
                <div>
                  <Label className="text-sm font-medium">Listing claimed</Label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    When off, owner sees a claim banner and can&apos;t edit
                    fields themselves.
                  </p>
                </div>
                <Switch
                  checked={form.claimed}
                  onCheckedChange={(v) => update({ claimed: v })}
                  data-testid="admin-edit-claimed"
                />
              </div>
            </>
          )}

          {!isBusiness && (
            <>
              <FieldRow>
                <Field label="Street (full address — admin only)" full>
                  <Input
                    value={form.street}
                    onChange={(e) => update({ street: e.target.value })}
                    placeholder="123 Oak Lane"
                    className="rounded-md"
                    data-testid="admin-edit-street"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">
                    Stored privately. Public profiles always strip the building
                    number — only the street name is shown to other users.
                  </p>
                </Field>
              </FieldRow>

              <FieldRow>
                <Field label="City">
                  <Input
                    value={form.city}
                    onChange={(e) => update({ city: e.target.value })}
                    className="rounded-md"
                    data-testid="admin-edit-city"
                  />
                </Field>
                <Field label="State">
                  <Input
                    value={form.state}
                    onChange={(e) => update({ state: e.target.value })}
                    className="rounded-md"
                    data-testid="admin-edit-state"
                  />
                </Field>
                <Field label="ZIP">
                  <Input
                    value={form.zip}
                    onChange={(e) => update({ zip: e.target.value })}
                    className="rounded-md"
                    data-testid="admin-edit-zip"
                  />
                </Field>
              </FieldRow>

              <div className="flex items-center justify-between border border-border rounded-md px-3 py-2.5 bg-gray-50">
                <div>
                  <Label className="text-sm font-medium">
                    Promotional message opt-in
                  </Label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Off = customer is unsubscribed from all business marketing
                    blasts.
                  </p>
                </div>
                <Switch
                  checked={form.wants_promo_emails}
                  onCheckedChange={(v) => update({ wants_promo_emails: v })}
                  data-testid="admin-edit-wants-promo"
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter className="mt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="rounded-[35px]"
            data-testid="admin-edit-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
            data-testid="admin-edit-save"
          >
            {saving ? (
              <>
                <Loader2 size={14} className="mr-1.5 animate-spin" /> Saving…
              </>
            ) : (
              <>
                <Save size={14} className="mr-1.5" /> Save changes
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({ children }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 [&>*:only-child]:sm:col-span-3">
      {children}
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "sm:col-span-3" : ""}>
      <Label className="text-xs font-mono uppercase tracking-wider text-gray-500">
        {label}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function buildInitialState(u) {
  if (!u) return {};
  return {
    name: u.name || "",
    email: u.email || "",
    // customer
    street: u.street_full || u.street || "",
    city: u.city || "",
    state: u.state || "",
    zip: u.zip || "",
    wants_promo_emails: u.wants_promo_emails !== false,
    // business
    business_name: u.business_name || "",
    business_address: u.business_address || "",
    business_city: u.business_city || "",
    business_state: u.business_state || "",
    business_zip: u.business_zip || "",
    description: u.description || "",
    website: u.website || "",
    phone: u.phone || "",
    contact_email: u.contact_email || "",
    date_established: u.date_established || "",
    price_tier: u.price_tier || "",
    claimed: u.claimed !== false,
    logo_data_url: u.logo_data_url || "",
  };
}

function buildPayload(form, user) {
  const out = {};
  const shared = ["name", "email"];
  const customer = ["street", "city", "state", "zip", "wants_promo_emails"];
  const business = [
    "business_name",
    "business_address",
    "business_city",
    "business_state",
    "business_zip",
    "description",
    "website",
    "phone",
    "contact_email",
    "date_established",
    "price_tier",
    "claimed",
    "logo_data_url",
  ];
  const keys =
    user.role === "business"
      ? [...shared, ...business]
      : [...shared, ...customer];
  keys.forEach((k) => {
    out[k] = form[k];
  });
  return out;
}
